import React, { useMemo, useState } from 'react';
import {
	Button,
	Checkbox,
	Collapse,
	Divider,
	FormControl,
	FormControlLabel,
	IconButton,
	Menu,
	MenuItem,
	Select,
	TextField,
	Typography,
} from '@mui/material';
import {
	Ban,
	CheckCircle,
	ChevronDown,
	ChevronUp,
	Clock,
	Filter,
	PhoneCall,
	PauseCircle,
	Search,
	Sparkles,
	TrendingUp,
	XCircle,
} from 'lucide-react';
import TuneIcon from '@mui/icons-material/Tune';
import CloseIcon from '@mui/icons-material/Close';
import './LeadsFilters.scss';

const STATUS_OPTIONS = [
	{
		value: 'newLeads',
		label: 'New Leads',
		className: 'status-new',
		icon: <Sparkles size={14} />,
	},
	{
		value: 'contacted',
		label: 'Contacted',
		className: 'status-hot',
		icon: <TrendingUp size={14} />,
	},
	{
		value: 'followUp',
		label: 'Follow Up',
		className: 'status-info',
		icon: <PhoneCall size={14} />,
	},
	{
		value: 'active',
		label: 'Active',
		className: 'status-warm',
		icon: <Clock size={14} />,
	},
	{
		value: 'onHold',
		label: 'On Hold',
		className: 'status-muted',
		icon: <PauseCircle size={14} />,
	},
	{
		value: 'onboarded',
		label: 'Onboarded',
		className: 'status-success',
		icon: <CheckCircle size={14} />,
	},
	{
		value: 'closed',
		label: 'Closed',
		className: 'status-danger',
		icon: <XCircle size={14} />,
	},
	{
		value: 'lost',
		label: 'Lost',
		className: 'status-danger-soft',
		icon: <Ban size={14} />,
	},
];

const SOURCE_OPTIONS = [
	{
		value: 'whatsapp',
		label: 'WhatsApp',
		className: 'source-whatsapp',
	},
	{
		value: 'email',
		label: 'Email',
		className: 'source-email',
	},
];

const DATE_OPTIONS = [
	{
		value: 'all',
		label: 'Any time',
	},
	{
		value: 'today',
		label: 'Today',
	},
	{
		value: '7days',
		label: 'Last 7 days',
	},
	{
		value: '30days',
		label: 'Last 30 days',
	},
	{
		value: '90days',
		label: 'Last 90 days',
	},
	{
		value: 'custom',
		label: 'Custom range',
	},
];

const SORT_OPTIONS = [
	{
		value: 'createTimeStamp',
		label: 'Created date',
	},
	{
		value: 'modifiedTimeStamp',
		label: 'Updated date',
	},
	{
		value: 'name',
		label: 'Name',
	},
];

const initialFilters = {
	statusList: [],
	sourceList: [],
	sourceTargetIdList: [],
	datePreset: 'all',
	customFrom: '',
	customTo: '',
	sortBy: 'createTimeStamp',
	sortAsc: false,
};

const initialSections = {
	status: true,
	source: true,
	date: false,
	sort: false,
};

export default function LeadsFilters({currentProjectId,pageNumber = 1,pageSize = 25,searchQuery = '',onApply, whatsappPhoneNumberList}) {

	const [anchorEl, setAnchorEl] = useState(null);
	const [filters, setFilters] = useState(initialFilters);
	const [openSections, setOpenSections] = useState(initialSections);
	const menuOpen = Boolean(anchorEl);

	const activeFilterCount = useMemo(() => {
		let count = 0;
		count += filters.statusList.length;
		count += filters.sourceList.length;
		if (filters.datePreset !== 'all') {
			count += 1;
		}
		if (filters.sortBy !== 'createTimeStamp' || filters.sortAsc !== false) {
			count += 1;
		}
		return count;
	}, [filters]);

	const handleOpen = (event) => {
		setAnchorEl(event.currentTarget);
	};

	const handleClose = () => {
		setAnchorEl(null);
	};

	const toggleSection = (section) => {
		setOpenSections((prev) => ({
			...prev,
			[section]: !prev[section],
		}));
	};

	const toggleStatus = (status) => {
		setFilters((prev) => ({
			...prev,
			statusList: prev.statusList.includes(status)
				? prev.statusList.filter((item) => item !== status)
				: [...prev.statusList, status],
		}));
	};

	const toggleSource = (source) => {
		setFilters((prev) => ({
			...prev,
			sourceList: prev.sourceList.includes(source)
				? prev.sourceList.filter((item) => item !== source)
				: [...prev.sourceList, source],
		}));
	};

	const toggleSourceTargetId = (sourceTargetId) => {
		setFilters((prev) => ({
			...prev,
			sourceTargetIdList: prev.sourceTargetIdList.includes(sourceTargetId)
				? prev.sourceTargetIdList.filter((item) => item !== sourceTargetId)
				: [...prev.sourceTargetIdList, sourceTargetId],
		}));
	};

	const getDateRange = () => {
		const now = new Date();
		switch (filters.datePreset) {
			case 'today': {
				const from = new Date();
				from.setHours(0, 0, 0, 0);
				return {
					from: from.getTime(),
					to: now.getTime(),
				};
			}
			case '7days':
				return {
					from: Date.now() - 7 * 24 * 60 * 60 * 1000,
					to: now.getTime(),
				};
			case '30days':
				return {
					from: Date.now() - 30 * 24 * 60 * 60 * 1000,
					to: now.getTime(),
				};
			case '90days':
				return {
					from: Date.now() - 90 * 24 * 60 * 60 * 1000,
					to: now.getTime(),
				};
			case 'custom':
				return {
					from: filters.customFrom
						? new Date(filters.customFrom).setHours(0, 0, 0, 0)
						: 0,
					to: filters.customTo
						? new Date(filters.customTo).setHours(23, 59, 59, 999)
						: now.getTime(),
				};
			default:
				return {
					from: 0,
					to: now.getTime(),
				};
		}
	};

	const buildPayload = (page = pageNumber) => {
		const dateRange = getDateRange();
		return {
			leadsFilter: {
				searchQuery: searchQuery?.trim() || '',
				projectIdList: currentProjectId ? [currentProjectId] : [],
				statusList: filters.statusList,
				sourceList: filters.sourceList,
				sourceTargetIdList: filters.sourceTargetIdList,
				sortBy: filters.sortBy,
				sortAsc: filters.sortAsc,
				createTimeStampFrom: dateRange.from,
				createTimeStampTo: dateRange.to,
			},
			page,
			size: pageSize,
		};
	};

	const handleApply = () => {
		onApply?.(buildPayload(pageNumber));
		handleClose();
	};

	const handleClear = () => {
		setFilters({
			...initialFilters,
		});
		onApply?.({
			leadsFilter: {
				searchQuery: searchQuery?.trim() || '',
				projectIdList: currentProjectId ? [currentProjectId] : [],
				statusList: [],
				source: [],
				sourceTargetIdList: [],
				sortBy: 'createTimeStamp',
				sortAsc: false,
				createTimeStampFrom: 0,
				createTimeStampTo: new Date().getTime(),
			},
			page: 1,
			size: pageSize,
		});
		handleClose();
	};

	const clearStatus = () => {
		setFilters((prev) => ({
			...prev,
			statusList: [],
		}));
	};

	const clearSource = () => {
		setFilters((prev) => ({
			...prev,
			sourceList: [],
		}));
	};

	const clearSourceTargetId = () => {
		setFilters((prev) => ({
			...prev,
			sourceTargetIdList: [],
		}));
	};

	const clearDate = () => {
		setFilters((prev) => ({
			...prev,
			datePreset: 'all',
			customFrom: '',
			customTo: '',
		}));
	};

	const clearSort = () => {
		setFilters((prev) => ({
			...prev,
			sortBy: 'createTimeStamp',
			sortAsc: false,
		}));
	};

	const getSectionCount = (section) => {
		switch (section) {
			case 'status':
				return filters.statusList.length;
			case 'source':
				return filters.sourceList.length;
			case 'date':
				return filters.datePreset !== 'all' ? 1 : 0;
			case 'sort':
				return filters.sortBy !== 'createTimeStamp' || filters.sortAsc !== false
					? 1
					: 0;
			default:
				return 0;
		}
	};

	const renderSectionHeader = ({ id, title, subtitle, count, isOpen, onClear }) => (
		<button
			type='button'
			className={`filter-section-header ${
				isOpen ? 'filter-section-header--open' : ''
			}`}
			onClick={() => toggleSection(id)}
		>
			<div className='filter-section-header__left'>
				<div className='filter-section-header__text'>
					<span className='filter-section-header__title'>{title}</span>
					{subtitle && (
						<span className='filter-section-header__subtitle'>{subtitle}</span>
					)}
				</div>
				{count > 0 && (
					<span className='filter-section-header__count'>{count}</span>
				)}
			</div>
			<div className='filter-section-header__right'>
				{count > 0 && onClear && (
					<span
						className='filter-section-header__clear'
						onClick={(event) => {
							event.stopPropagation();
							onClear();
						}}
					>
						Clear
					</span>
				)}
				{isOpen ? <ChevronUp size={17} /> : <ChevronDown size={17} />}
			</div>
		</button>
	);

	return (
		<div className='lead-filter-wrapper'>
			<Button
				className={`lead-filter-button ${
					menuOpen ? 'lead-filter-button--active' : ''
				}`}
				onClick={handleOpen}
				startIcon={<TuneIcon />}
				endIcon={
					<ChevronDown
						size={16}
						className={
							menuOpen
								? 'filter-button-chevron filter-button-chevron--open'
								: 'filter-button-chevron'
						}
					/>
				}
			>
				<span>Filters</span>
				{activeFilterCount > 0 && (
					<span className='lead-filter-count'>{activeFilterCount}</span>
				)}
			</Button>
			<Menu
				anchorEl={anchorEl}
				open={menuOpen}
				onClose={handleClose}
				className='lead-filter-menu'
				anchorOrigin={{
					vertical: 'bottom',
					horizontal: 'right',
				}}
				transformOrigin={{
					vertical: 'top',
					horizontal: 'right',
				}}
				slotProps={{
					paper: {
						className: 'lead-filter-menu-paper',
					},
				}}
			>
				<div className='filter-menu-content'>
					{/* Header */}
					<div className='filter-menu-header'>
						<div className='filter-menu-header__left'>
							<div className='filter-menu-header__icon'>
								<Filter size={17} />
							</div>
							<div>
								<Typography className='filter-menu-title'>
									Filter leads
								</Typography>
								<Typography className='filter-menu-subtitle'>
									Refine your results
								</Typography>
							</div>
						</div>
						<IconButton
							size='small'
							className='filter-menu-close'
							onClick={handleClose}
						>
							<CloseIcon fontSize='small' />
						</IconButton>
					</div>
					<Divider />
					{/* Scrollable content */}
					<div className='filter-menu-scroll'>
						{/* Status */}
						<div className='filter-section'>
							{renderSectionHeader({
								id: 'status',
								title: 'Lead status',
								subtitle:
									filters.statusList.length > 0
										? `${filters.statusList.length} selected`
										: 'Select one or more',
								count: getSectionCount('status'),
								isOpen: openSections.status,
								onClear: clearStatus,
							})}
							<Collapse in={openSections.status} timeout='auto'>
								<div className='filter-section-content'>
									<div className='status-list'>
										{STATUS_OPTIONS.map((item) => {
											const checked = filters.statusList.includes(item.value);
											return (
												<button
													type='button'
													key={item.value}
													className={`status-item ${
														checked ? 'status-item--selected' : ''
													}`}
													onClick={() => toggleStatus(item.value)}
												>
													<span className={`lead-status ${item.className}`}>
														<Checkbox
															size='small'
															checked={checked}
															onChange={() => toggleStatus(item.value)}
															onClick={(event) => event.stopPropagation()}
														/>
														{item.icon}
														<span>{item.label}</span>
													</span>
												</button>
											);
										})}
									</div>
								</div>
							</Collapse>
						</div>
						<Divider />
						{/* Source */}
						{
//						<div className='filter-section'>
//							{renderSectionHeader({
//								id: 'source',
//								title: 'Lead source',
//								subtitle:
//									filters.sourceList.length > 0
//										? `${filters.sourceList.length} selected`
//										: 'Where the lead came from',
//								count: getSectionCount('source'),
//								isOpen: openSections.source,
//								onClear: clearSource,
//							})}
//							<Collapse in={openSections.source} timeout='auto'>
//								<div className='filter-section-content'>
//									<div className='source-list'>
//										{SOURCE_OPTIONS.map((item) => {
//											const checked = filters.sourceList.includes(item.value);
//											return (
//												<button
//													type='button'
//													key={item.value}
//													className={`source-item ${
//														checked ? 'source-item--selected' : ''
//													}`}
//													onClick={() => toggleSource(item.value)}
//												>
//													<Checkbox
//														size='small'
//														checked={checked}
//														onClick={(event) => event.stopPropagation()}
//														onChange={() => toggleSource(item.value)}
//													/>
//													<span className={`source-dot ${item.className}`} />
//													<span className='source-item__label'>
//														{item.label}
//													</span>
//												</button>
//											);
//										})}
//									</div>
//								</div>
//							</Collapse>
//						</div>
						}
						{
//						    Source target id
						}
						<div className='filter-section'>
							{renderSectionHeader({
								id: 'sourceTargetId',
								title: 'Lead source target',
								subtitle:
									filters.sourceList.length > 0
										? `${filters.sourceList.length} selected`
										: 'Where the lead came from',
								count: getSectionCount('sourceTargetId'),
								isOpen: openSections.sourceTargetId,
								onClear: clearSourceTargetId,
							})}
							<Collapse in={openSections.sourceTargetId} timeout='auto'>
								<div className='filter-section-content'>
									<div className='source-list'>
										{whatsappPhoneNumberList.map((item) => {
											const checked = filters.sourceTargetIdList.includes(item.id);
											return (
												<button
													type='button'
													key={item.id}
													className={`source-item ${
														checked ? 'source-item--selected' : ''
													}`}
													onClick={() => toggleSourceTargetId(item.id)}
												>
													<Checkbox
														size='small'
														checked={checked}
														onClick={(event) => event.stopPropagation()}
														onChange={() => toggleSourceTargetId(item.id)}
													/>
													<span className={`source-dot ${item.className}`} />
													<span className='source-item__label'>
														{item.name}
													</span>
												</button>
											);
										})}
									</div>
								</div>
							</Collapse>
						</div>
                        <Divider />
						{/* Created Date */}
						<div className='filter-section'>
							{renderSectionHeader({
								id: 'date',
								title: 'Created date',
								subtitle:
									filters.datePreset === 'all'
										? 'Any time'
										: DATE_OPTIONS.find(
												(item) => item.value === filters.datePreset,
											)?.label,
								count: getSectionCount('date'),
								isOpen: openSections.date,
								onClear: clearDate,
							})}
							<Collapse in={openSections.date} timeout='auto'>
								<div className='filter-section-content'>
									<div className='filter-field'>
										<label className='filter-field__label'>Date range</label>
										<FormControl
											fullWidth
											size='small'
											className='filter-field__control'
										>
											<Select
												value={filters.datePreset}
												onChange={(event) =>
													setFilters((prev) => ({
														...prev,
														datePreset: event.target.value,
													}))
												}
												displayEmpty
												className='filter-select'
												MenuProps={{
													PaperProps: {
														className: 'filter-select-menu',
													},
												}}
											>
												{DATE_OPTIONS.map((option) => (
													<MenuItem
														key={option.value}
														value={option.value}
														className='filter-select-menu-item'
													>
														{option.label}
													</MenuItem>
												))}
											</Select>
										</FormControl>
									</div>
									{filters.datePreset === 'custom' && (
										<div className='custom-date-fields'>
											<div className='filter-field'>
												<label className='filter-field__label'>From</label>
												<TextField
													fullWidth
													type='date'
													size='small'
													value={filters.customFrom}
													onChange={(event) =>
														setFilters((prev) => ({
															...prev,
															customFrom: event.target.value,
														}))
													}
													className='custom-date-field'
													inputProps={{
														max: filters.customTo || undefined,
													}}
												/>
											</div>
											<div className='filter-field'>
												<label className='filter-field__label'>To</label>
												<TextField
													fullWidth
													type='date'
													size='small'
													value={filters.customTo}
													onChange={(event) =>
														setFilters((prev) => ({
															...prev,
															customTo: event.target.value,
														}))
													}
													className='custom-date-field'
													inputProps={{
														min: filters.customFrom || undefined,
													}}
												/>
											</div>
										</div>
									)}
								</div>
							</Collapse>
						</div>
						<Divider />
						{/* Sort */}
						<div className='filter-section'>
							{renderSectionHeader({
								id: 'sort',
								title: 'Sort by',
								subtitle: SORT_OPTIONS.find(
									(item) => item.value === filters.sortBy,
								)?.label,
								count: getSectionCount('sort'),
								isOpen: openSections.sort,
								onClear: clearSort,
							})}
							<Collapse in={openSections.sort} timeout='auto'>
								<div className='filter-section-content'>
									<div className='filter-field'>
										<label className='filter-field__label'>
											Sort results by
										</label>
										<FormControl
											fullWidth
											size='small'
											className='filter-field__control'
										>
											<Select
												value={filters.sortBy}
												onChange={(event) =>
													setFilters((prev) => ({
														...prev,
														sortBy: event.target.value,
													}))
												}
												className='filter-select'
												MenuProps={{
													PaperProps: {
														className: 'filter-select-menu',
													},
												}}
											>
												{SORT_OPTIONS.map((option) => (
													<MenuItem
														key={option.value}
														value={option.value}
														className='filter-select-menu-item'
													>
														{option.label}
													</MenuItem>
												))}
											</Select>
										</FormControl>
									</div>
									<div className='sort-direction'>
										<FormControlLabel
											control={
												<Checkbox
													size='small'
													checked={filters.sortAsc}
													onChange={(event) =>
														setFilters((prev) => ({
															...prev,
															sortAsc: event.target.checked,
														}))
													}
												/>
											}
											label={filters.sortAsc ? 'Oldest first' : 'Newest first'}
										/>
									</div>
								</div>
							</Collapse>
						</div>
					</div>
					{/* Footer */}
					<div className='filter-menu-footer'>
						<Button className='clear-filter-button' onClick={handleClear}>
							Clear all
						</Button>
						<div className='filter-footer-actions'>
							<Button className='cancel-filter-button' onClick={handleClose}>
								Cancel
							</Button>
							<Button className='apply-filter-button' onClick={handleApply}>
								Apply filters
							</Button>
						</div>
					</div>
				</div>
			</Menu>
		</div>
	);
}
